import {
  sortedAutoRtRows,
  hasForceDisplayFormat,
  matchesRtRowCondition,
  matchesAutoRtExtraGate,
  resolveRequestableMode,
  buildAutoRtDialogInfo
} from '@/plugins/rt-dialog-migration'

type RtCondition = {
  client_id: string
  permission_type: number
  request_able: boolean
}

type UnknownRecord = Record<string, any>

const createEmptyAutoRTInfo = (): UnknownRecord => ({
  logoImage: '',
  clientId: '',
  dialogName: '',
  clientName: '',
  termurl: '',
  privacyurl: '',
  occupationurl: '',
  handlingurl: '',
  clientServiceName: '',
  privacyLabel: '',
  showTrailingBr: false,
  permissionType: 0,
  oldPermissionType: 0,
  googleConversationId: null,
  googleConversationLabel: null
})

type DownloadItemVm = {
  $firebase: { user: { uid: string; email?: string } }
  $deviceLib: { isMobileDevice: boolean }
  $site: { appFlag: string }
  $axios: { post: (url: string, data: FormData, config: { timeout: number }) => Promise<any> }
  $varuna: { endPoints: { displayDialog: string } }
  $globalVar: UnknownRecord
}

export function useDownloadItemAutoRt (vm: DownloadItemVm) {
  const hasRequestable = (rtconditions: RtCondition[], clientId: string, permissionType: number) => {
    const matched = rtconditions.find(item =>
      item.client_id === clientId && item.permission_type === permissionType
    )
    return !!(matched && matched.request_able)
  }

  const hasOldAndNewRequestable = (rtconditions: RtCondition[], clientId: string, newPermissionType: number, oldPermissionType: number) => {
    return hasRequestable(rtconditions, clientId, oldPermissionType) &&
      hasRequestable(rtconditions, clientId, newPermissionType)
  }

  const notifyDisplay = (formatId: string, clientId: string, permissionType: number) => {
    const formData = new FormData()
    formData.append('uuid', vm.$firebase.user.uid)
    formData.append('format_id', formatId)
    formData.append('dialog_name', 'auto_rt')
    formData.append('client_id', clientId)
    formData.append('permission_type', String(permissionType))
    formData.append('device_flg', vm.$deviceLib.isMobileDevice ? 'SP' : 'PC')
    formData.append('version', '2')
    formData.append('site_flag', 'r')
    formData.append('app_flag', vm.$site.appFlag)
    vm.$axios.post(vm.$varuna.endPoints.displayDialog, formData, { timeout: 60000 }).catch((error: unknown) => {
      console.error('[useDownloadItemAutoRt] notifyDisplay failed', {
        formatId,
        clientId,
        permissionType,
        error
      })
    })
  }

  const selectAutoRT = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord): UnknownRecord | null => {
    // ヤマト住建（is_BehindRT）向けには auto_rt を一切表示しない。
    if (vm.$globalVar.is_BehindRT && vm.$globalVar.is_BehindRT()) {
      return null
    }

    // shokumukeirekisho フォーマット時は keyword/grade/foreignStudent の判定対象を otherFormatData に切替える。
    const isShoku = vm.$globalVar.isShokumukeirekiFormat(formatId)
    const conditionFormatData: UnknownRecord = isShoku ? otherFormatData : formatData

    const conditionCtx = {
      formatId,
      formatData: conditionFormatData,
      basicData,
      addressCheck: (bd: unknown, prefectures: string[]) => vm.$globalVar.addressCheck(bd, prefectures),
      keywordCheckRirekisho: (fd: unknown, keywords: string[]) => vm.$globalVar.keywordCheckRirekisho(fd, keywords),
      keywordCheckShokumukeirekisho: (fd: unknown, keywords: string[]) => vm.$globalVar.keywordCheckShokumukeirekisho(fd, keywords)
    }
    const gateCtx = {
      basicData,
      formatData: conditionFormatData,
      userEmail: (vm.$firebase && vm.$firebase.user && vm.$firebase.user.email) || ''
    }

    const rows = sortedAutoRtRows(rtconditions)
    for (const row of rows) {
      const clientId = String(row.client_id || '')
      const spec = resolveRequestableMode(row)
      const requestableOk = spec.mode === 'NewOnly'
        ? hasRequestable(rtconditions, clientId, spec.newPermissionType)
        : hasOldAndNewRequestable(rtconditions, clientId, spec.newPermissionType, spec.oldPermissionType)
      if (!requestableOk) continue

      const conditionMatched = hasForceDisplayFormat(row, formatId) ||
        (matchesRtRowCondition(row, conditionCtx) && matchesAutoRtExtraGate(row, gateCtx))
      if (!conditionMatched) continue

      return buildAutoRtDialogInfo(row)
    }
    return null
  }

  const evaluateAutoRT = (rtconditions: RtCondition[], basicData: UnknownRecord, formatId: string, formatData: UnknownRecord, otherFormatData: UnknownRecord) => {
    // 単一の真実源: 評価ごとに状態をリセットし、選定結果(どのRTを出すか)から表示・状態を一括適用する
    vm.$globalVar.autoRTInfo = createEmptyAutoRTInfo()
    vm.$globalVar.initAutoRT()
    vm.$globalVar.permissionType4RT = 0
    vm.$globalVar.oldPermissionType4RT = 0

    const selected = selectAutoRT(rtconditions, basicData, formatId, formatData, otherFormatData)
    if (!selected) {
      return null
    }

    vm.$globalVar.autoRTInfo = selected
    vm.$globalVar.isAutoRTable = true
    vm.$globalVar.isAutoRT4Rtable = true
    vm.$globalVar.permissionType4RT = Number(selected.permissionType) || 0
    vm.$globalVar.oldPermissionType4RT = Number(selected.oldPermissionType) || 0
    return selected
  }

  return {
    evaluateAutoRT,
    selectAutoRT,
    notifyDisplay
  }
}
